import React, { useState, useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import set from "lodash/set";
import { validateExternalIdRequiresArn } from "integrations/aws/utils/awsValidation";
import { InputOptionalInfo, Select } from "components/common";
import { Alert, BootstrapModalForm, Button, Input } from "components/bootstrap";
import EntityCreateShareFormGroup from "components/permissions/EntityCreateShareFormGroup";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { saveDataLakeConnector } from "./useDataLakeConnectors";
import useDataLakeConnectorById from "./useDataLakeConnectorById";
import useDataLakeConnectorRegions from "./useDataLakeConnectorRegions";
const SubHeadline = styled.h4(
  ({ theme }) => css`
    margin-top: ${theme.spacings.md};
    margin-bottom: ${theme.spacings.xs};
  `
);
const EditConnectorModal = ({ connectorId = void 0, onRefetch, onCancel }) => {
  var _a, _b, _c, _d, _e, _f;
  const initialValues = useMemo(
    () => ({
      id: connectorId,
      aws_region: "",
      aws_access_key: "",
      aws_iam_role: "",
      aws_external_id: "",
      stream_id: "",
      stream_title: connectorId ? void 0 : "",
      stream_description: connectorId ? void 0 : "",
      data_lake_name: "",
      s3_output_bucket: "",
      aws_secret_key: {
        set_value: "",
        is_set: false
      },
      share_request: void 0
    }),
    [connectorId]
  );
  const [connector, setConnector] = useState(initialValues);
  const [externalIdError, setExternalIdError] = useState(void 0);
  const { data } = useDataLakeConnectorById(connectorId);
  const { data: regions } = useDataLakeConnectorRegions();
  const sendTelemetry = useSendTelemetry();
  const regionOptions = regions == null ? void 0 : regions.map((region) => ({ label: region, value: region }));
  const savedConnector = connectorId ? {
    ...data != null ? data : {},
    aws_secret_key: {
      is_set: (_a = connector == null ? void 0 : connector.aws_secret_key) == null ? void 0 : _a.is_set,
      set_value: ""
    },
    stream_title: void 0,
    stream_description: void 0,
    share_request: void 0
  } : initialValues;
  const isConnectorFormDirty = !isEqual(connector, savedConnector);
  useEffect(() => {
    if (data) {
      setConnector({
        ...initialValues,
        ...data,
        aws_secret_key: {
          ...initialValues.aws_secret_key,
          ...data.aws_secret_key
        },
        share_request: void 0
      });
    }
  }, [data, initialValues]);
  const updateConnectorField = (event) => {
    const newConnector = cloneDeep(connector);
    set(newConnector, event.target.name, event.target.value);
    setConnector(newConnector);
    setExternalIdError(void 0);
  };
  const updateConnectorFieldByName = (name, value) => {
    const newConnector = cloneDeep(connector);
    set(newConnector, name, value);
    setConnector(newConnector);
  };
  const toggleResetAwsSecretKey = () => {
    setConnector({
      ...connector,
      aws_secret_key: {
        ...connector.aws_secret_key,
        is_set: !connector.aws_secret_key.is_set
      }
    });
  };
  const onSubmitForm = async () => {
    sendTelemetry(
      connectorId ? TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.EDIT_CONNECTOR_MODAL_SAVE_CLICKED : TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.ADD_CONNECTOR_MODAL_SAVE_CLICKED,
      {}
    );
    const extIdValidationError = validateExternalIdRequiresArn(connector == null ? void 0 : connector.aws_iam_role, connector == null ? void 0 : connector.aws_external_id);
    if (extIdValidationError) {
      setExternalIdError(extIdValidationError);
      return;
    }
    setExternalIdError(void 0);
    await saveDataLakeConnector(connector);
    await onRefetch();
    onCancel();
  };
  return /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: true,
      title: connectorId ? "Edit Connector" : "Add Connector",
      onSubmitForm,
      onCancel,
      submitButtonDisabled: !isConnectorFormDirty,
      submitButtonText: "Save"
    },
    /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "Each connector must be associated with a system managed stream to which data retrieved from this connector will be stored. It is not possible to add Stream Rules, Pipeline Rules, Routing Destinations or Filter Rules to a Stream associated with a Connector."),
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "data_lake_name",
        type: "text",
        label: "Data Lake Name",
        name: "data_lake_name",
        value: connector == null ? void 0 : connector.data_lake_name,
        onChange: updateConnectorField,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "s3_output_bucket",
        type: "text",
        label: "S3 Output Bucket",
        name: "s3_output_bucket",
        placeholder: "s3://",
        value: connector == null ? void 0 : connector.s3_output_bucket,
        onChange: updateConnectorField,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(Input, { label: "AWS Region", help: "The AWS Region your service is running in." }, /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "aws_region",
        name: "aws_region",
        placeholder: "Select AWS Region",
        options: regionOptions || [],
        onChange: (option) => {
          updateConnectorFieldByName("aws_region", option);
        },
        value: (connector == null ? void 0 : connector.aws_region) || "",
        required: true
      }
    )), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws_access_key",
        type: "text",
        label: "AWS Access Key",
        placeholder: "AK****************",
        help: 'Your AWS Key should be a 20-character long, alphanumeric string that starts with the letters "AK".',
        name: "aws_access_key",
        value: connector == null ? void 0 : connector.aws_access_key,
        onChange: updateConnectorField,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws_secret_key",
        type: "password",
        label: "AWS Secret Key",
        help: "Your AWS Secret is usually a 40-character long, base-64 encoded string.",
        name: "aws_secret_key.set_value",
        placeholder: ((_b = connector == null ? void 0 : connector.aws_secret_key) == null ? void 0 : _b.is_set) ? "****************" : void 0,
        value: ((_c = connector == null ? void 0 : connector.aws_secret_key) == null ? void 0 : _c.is_set) ? "" : (_d = connector == null ? void 0 : connector.aws_secret_key) == null ? void 0 : _d.set_value,
        onChange: updateConnectorField,
        disabled: (_e = connector == null ? void 0 : connector.aws_secret_key) == null ? void 0 : _e.is_set,
        buttonAfter: connectorId ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: toggleResetAwsSecretKey }, ((_f = connector == null ? void 0 : connector.aws_secret_key) == null ? void 0 : _f.is_set) ? "Reset" : "Undo Reset") : void 0,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws_iam_role",
        type: "text",
        label: "AWS IAM Role",
        help: "AWS IAM Role to be assumed for the connector (optional)",
        name: "aws_iam_role",
        placeholder: "arn:aws:iam::****************",
        value: connector == null ? void 0 : connector.aws_iam_role,
        onChange: updateConnectorField
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws_external_id",
        type: "text",
        label: "AWS External ID",
        help: "External ID for the assume role, used to prevent the confused deputy problem. Only applies when an AWS IAM Role is provided. (optional)",
        name: "aws_external_id",
        value: connector == null ? void 0 : connector.aws_external_id,
        onChange: updateConnectorField,
        error: externalIdError
      }
    ), !connectorId && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SubHeadline, null, "Create New Stream"), /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement("p", null, "This stream will be permanently assigned to this connector."), /* @__PURE__ */ React.createElement("p", null, "Users without the administrator role must to be granted permission to view this stream in order to select this connector on the Preview page.")), /* @__PURE__ */ React.createElement(
      Input,
      {
        label: "Stream Title",
        name: "stream_title",
        id: "stream_title",
        type: "text",
        help: "A descriptive name of the new stream",
        value: connector == null ? void 0 : connector.stream_title,
        onChange: updateConnectorField,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Stream Description ", /* @__PURE__ */ React.createElement(InputOptionalInfo, null)),
        name: "stream_description",
        id: "stream_description",
        type: "text",
        help: "What kind of messages are routed into this stream?",
        value: connector == null ? void 0 : connector.stream_description,
        onChange: updateConnectorField
      }
    ), /* @__PURE__ */ React.createElement(SubHeadline, null, "Share Stream"), /* @__PURE__ */ React.createElement(
      EntityCreateShareFormGroup,
      {
        description: "Search for a User or Team to add as collaborator on this stream.",
        entityType: "stream",
        entityTitle: connector == null ? void 0 : connector.stream_title,
        defaultSharePayload: connector == null ? void 0 : connector.share_request,
        onSetEntityShare: (payload) => updateConnectorFieldByName("share_request", payload)
      }
    )))
  );
};
export default EditConnectorModal;
